import React, { createContext, useContext, useEffect } from "react";
import {
  Easing,
  type SharedValue,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming,
  runOnUI,
} from "react-native-reanimated";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useSelector } from "react-redux";
import { Dimensions, Platform } from "react-native";
import { useTheme } from "styled-components/native";
import * as ScreenOrientation from "expo-screen-orientation";

type ScrollContextTuple = [
  SharedValue<number>,
  () => void,
  any,
  any,
  any,
  any,
  SharedValue<number>,
  SharedValue<number>,
  SharedValue<number>,
  (offset: number) => void
];

// @ts-ignore
const INITIAL_VALUE: ScrollContextTuple = [{ value: 0 }, () => {}];

export const ScrollContext = createContext<ScrollContextTuple>(INITIAL_VALUE);

export const useScrollContext = () => useContext(ScrollContext);

export const ScrollContextProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { top } = useSafeAreaInsets();
  const scrollValue = useSharedValue(0);
  const previousScrollValue = useSharedValue(0);
  const binaryScrollValue = useSharedValue(0);
  const searchResultSavedOffset = useSharedValue(0);
  const deviceHeight = useSharedValue(Dimensions.get("window").height);
  const navigationHeight = useSelector(
    (state: any) => state.appState.navigationHeight
  );
  const theme = useTheme();

  const forceResetOnUI = () => {
    "worklet";
    scrollValue.value = 0;
    previousScrollValue.value = 1000;
    binaryScrollValue.value = 0;
  };

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const subscription = ScreenOrientation.addOrientationChangeListener(() => {
      timeoutId = setTimeout(() => {
        deviceHeight.value = Dimensions.get("window").height;
        timeoutId = null;
      }, 1000);
    });
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      ScreenOrientation.removeOrientationChangeListener(subscription);
    };
  }, []);

  const handleReset = () => {
    if (Platform.OS === "ios") {
      runOnUI(forceResetOnUI)();
    }

    scrollValue.value = 0;
    previousScrollValue.value = 1000;
    binaryScrollValue.value = 0;

    scrollValue.value = withTiming(0, {
      duration: 0,
      easing: Easing.out(Easing.cubic),
    });
    previousScrollValue.value = withTiming(0, {
      duration: 0,
      easing: Easing.out(Easing.cubic),
    });
    binaryScrollValue.value = withTiming(0, {
      duration: 0,
      easing: Easing.out(Easing.cubic),
    });
  };

  const height = useSharedValue(navigationHeight);
  const shouldHideItems = useDerivedValue(() =>
    withTiming(scrollValue.value > 0.2 ? 1 : 0, {
      duration: 550,
      easing: Easing.out(Easing.cubic),
    })
  );

  useEffect(() => {
    if (navigationHeight > 0) {
      height.value = navigationHeight;
    }
  }, [navigationHeight]);

  const isMobile = theme.isMobile;

  const animatedStyle = useAnimatedStyle(() => ({
    marginTop: withTiming(
      shouldHideItems.value
        ? top
          ? isMobile
            ? top
            : height.value
          : 0
        : height.value || 0,
      {
        duration: 250,
        easing: Easing.out(Easing.cubic),
      }
    ),
    height: isMobile
      ? withTiming(
          shouldHideItems.value === 0
            ? deviceHeight.value - height.value || 0
            : deviceHeight.value,
          {
            duration: 550,
            easing: Easing.out(Easing.cubic),
          }
        )
      : deviceHeight.value - height.value,
  }));

  const customTopTabBoxAnimatedStyle = useAnimatedStyle(() => ({
    height: withTiming(scrollValue.value > 0.2 ? 0 : 24, {
      duration: 250,
      easing: Easing.out(Easing.cubic),
    }),
    overflow: "hidden",
  }));

  const summaryBoxAnimatedStyle = useAnimatedStyle(() => ({
    height: withTiming(scrollValue.value > 0.2 ? 56 : 56, {
      duration: 250,
      easing: Easing.out(Easing.cubic),
    }),
    overflow: "hidden",
  }));

  return (
    <ScrollContext.Provider
      value={[
        scrollValue,
        handleReset,
        animatedStyle,
        shouldHideItems,
        customTopTabBoxAnimatedStyle,
        summaryBoxAnimatedStyle,
        previousScrollValue,
        binaryScrollValue,
        searchResultSavedOffset,
      ]}
    >
      {children}
    </ScrollContext.Provider>
  );
};
